import { useCallback, useEffect, useRef, useState } from 'react';
import type { Calibration } from '@erectus/shared';
import { attachStream, CameraError, openCamera, type CameraFacing, type CameraStream } from '@/lib/camera.js';
import { loadLandmarker } from '@/pose/landmarker.js';
import { track } from '@/lib/analytics.js';
import { breadcrumb, reportError } from '@/lib/crashlytics.js';
import { currentImageRoll } from '@/lib/motion.js';
import {
  SessionRecorder,
  type PauseReason,
  type RecorderError,
  type RecorderPhase,
  type RecorderTick,
} from '@/session/recorder.js';

/**
 * One hook for "camera + pose loop", shared by Capture Setup, the live session
 * and the debug page. Owning the lifecycle in one place is what guarantees the
 * camera track is actually stopped when the screen unmounts.
 *
 * Camera first, then the model — and the order is the point.
 *
 * Starting the 5.7 MB model "in parallel" did not overlap anything: its
 * initialisation (WASM compile, graph setup, GPU shader compile) runs on the
 * main thread, so the camera promise and the first video frame queued behind
 * it and "Opening camera" stayed up for the whole model load — seconds on a
 * phone, every first entry. Now the picture is attached and painted first,
 * and the model initialises while the user is already looking at the preview.
 * The two readiness flags stay separate so the UI can say which one it is
 * waiting for. Timings go to the console (`[capture]`, visible in Xcode /
 * Android Studio logs on native builds).
 */
export interface UseCaptureOptions {
  sessionId: string;
  /** Enables the on-device artifact classifier for users inside the rollout. */
  uid?: string;
  sampleFps: number;
  poseModel: 'lite' | 'full';
  calibration?: Calibration | null;
  /** Which camera to open. Defaults to the rear one. */
  facing?: CameraFacing;
  /** Start the pose loop immediately (setup/debug) or wait for start(). */
  autoPrepare?: boolean;
  /** Told whenever the active camera changes, so the choice can be persisted. */
  onFacingChange?(facing: CameraFacing): void;
}

export interface UseCaptureResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  tick: RecorderTick | null;
  phase: RecorderPhase;
  pauseReason: PauseReason | null;
  error: { code: string; message: string } | null;
  facing: CameraFacing;
  /** Picture is on screen. */
  streamReady: boolean;
  /**
   * True once the <video> has painted a real frame. Until then the preview is
   * kept hidden: WebKit can paint the first frames before object-fit applies,
   * which showed as dark bands above and below the picture.
   */
  previewReady: boolean;
  /** Pose model loaded; measurement is running. */
  modelReady: boolean;
  /** Both of the above. */
  ready: boolean;
  start(): Promise<void>;
  /** 'user' by default; the rest break pauses with 'break'. */
  pause(reason?: PauseReason): void;
  resume(): void;
  stop(): Promise<void>;
  flipCamera(): Promise<void>;
  calibrate(): Promise<Calibration | null>;
  disagreementCount(): number;
}

/** No frame for this long while visible = the stream stalled. */
const STREAM_STALL_MS = 6000;
/** Reopen attempts in a row before telling the user. */
const MAX_STREAM_RECOVERIES = 3;

/** Calls `onEnded` when the camera track ends on its own (not on our stop()). */
function onTrackEnded(stream: MediaStream, onEnded: () => void): void {
  for (const track of stream.getVideoTracks()) track.addEventListener('ended', onEnded, { once: true });
}

/** Calls `done` after the video has presented a frame and the page has laid it out twice. */
function revealAfterFirstFrame(video: HTMLVideoElement, done: () => void): void {
  const settle = (): void => {
    requestAnimationFrame(() => requestAnimationFrame(done));
  };
  if (typeof video.requestVideoFrameCallback === 'function') video.requestVideoFrameCallback(() => settle());
  else if (video.readyState >= 2) settle();
  else video.addEventListener('loadeddata', settle, { once: true });
}

export function useCapture(options: UseCaptureOptions): UseCaptureResult {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const recorderRef = useRef<SessionRecorder | null>(null);
  const streamRef = useRef<CameraStream | null>(null);
  const [tick, setTick] = useState<RecorderTick | null>(null);
  const [phase, setPhase] = useState<RecorderPhase>('idle');
  const [pauseReason, setPauseReason] = useState<PauseReason | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [facing, setFacing] = useState<CameraFacing>(options.facing ?? 'environment');
  const [streamReady, setStreamReady] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [modelReady, setModelReady] = useState(false);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const boot = useCallback(async (nextFacing: CameraFacing) => {
    setError(null);
    setStreamReady(false);
    setPreviewReady(false);
    setModelReady(false);

    const current = optionsRef.current;
    const t0 = performance.now();
    // A flip replaces the recorder; the old one must stop sampling first.
    recorderRef.current?.dispose();
    recorderRef.current = null;

    try {
      const camera = await openCamera(nextFacing);
      const tOpen = performance.now();
      streamRef.current?.stop();
      streamRef.current = camera;
      const video = videoRef.current;
      if (!video) return;
      await attachStream(video, camera.stream);
      onTrackEnded(camera.stream, () => {
        if (streamRef.current === camera) void recoverRef.current();
      });
      setStreamReady(true);
      revealAfterFirstFrame(video, () => setPreviewReady(true));
      console.info(
        `[capture] camera open ${Math.round(tOpen - t0)}ms, first frame ${Math.round(performance.now() - t0)}ms`,
      );
    } catch (cameraError) {
      void reportError('camera-open', cameraError, { facing: nextFacing });
      if (cameraError instanceof CameraError) {
        setError({ code: cameraError.code, message: cameraError.message });
      } else {
        setError({ code: 'camera_missing', message: 'The camera could not be started.' });
      }
      return;
    }

    // Let the preview paint before the model takes the main thread.
    await nextPaint();
    const tModel = performance.now();
    const loaded = await loadLandmarker(current.poseModel).catch((modelError: unknown) => {
      void reportError('model-load', modelError, { model: current.poseModel });
      setError({
        code: 'model_load',
        message:
          modelError instanceof Error
            ? modelError.message
            : 'Could not load the posture model. Reload and try again.',
      });
      return null;
    });
    if (!loaded) return;
    console.info(
      `[capture] pose model ${loaded.model}/${loaded.delegate} ready in ${Math.round(performance.now() - tModel)}ms`,
    );

    const video = videoRef.current;
    if (!video) return;
    const recorder = new SessionRecorder({
      sessionId: current.sessionId,
      ...(current.uid ? { uid: current.uid } : {}),
      video,
      plannedMin: 0,
      sampleFps: current.sampleFps,
      poseModel: current.poseModel,
      calibration: current.calibration ?? null,
      onTick: setTick,
      onPhase: (nextPhase, reason) => {
        setPhase(nextPhase);
        setPauseReason(reason ?? null);
      },
      level: () => currentImageRoll(facingRef.current),
      onError: (recorderError: RecorderError) => {
        setError(recorderError);
        void reportError('capture', new Error(recorderError.message), { code: recorderError.code });
      },
      onRecovery: (ok) => {
        track('capture_recovered', { kind: 'tracking', ok });
        breadcrumb(`pose model rebuilt: ${ok ? 'ok' : 'failed'}`);
      },
    });
    recorderRef.current = recorder;
    await recorder.prepare();
    setModelReady(true);
  }, []);

  // ── stream recovery ───────────────────────────────────────────────────────
  // iOS can end or stall the camera under memory pressure (and the preview then
  // paints wrongly — the navy bands). Reopen the same camera under the same
  // <video> and keep the recorder, so a running session carries on.
  const recoveringRef = useRef(false);
  const recoverRef = useRef<() => Promise<void>>(async () => undefined);
  const recoveriesRef = useRef(0);
  const facingRef = useRef(facing);
  facingRef.current = facing;

  const recoverStream = useCallback(async () => {
    const video = videoRef.current;
    if (recoveringRef.current || !video || !recorderRef.current) return;
    recoveringRef.current = true;
    recoveriesRef.current += 1;
    try {
      if (recoveriesRef.current > MAX_STREAM_RECOVERIES) throw new Error('camera keeps dropping');
      const camera = await openCamera(facingRef.current);
      streamRef.current?.stop();
      streamRef.current = camera;
      await attachStream(video, camera.stream);
      onTrackEnded(camera.stream, () => {
        if (streamRef.current === camera) void recoverRef.current();
      });
      recorderRef.current?.restartLoop();
      console.info('[capture] camera stream recovered');
      track('capture_recovered', { kind: 'stream', ok: true });
      breadcrumb('camera stream reopened');
    } catch (streamError) {
      track('capture_recovered', { kind: 'stream', ok: false });
      void reportError('camera-recovery', streamError, { facing: facingRef.current });
      setError({
        code: 'lost_tracking',
        message: 'The camera stopped and could not restart. Close this screen and open it again.',
      });
    } finally {
      recoveringRef.current = false;
    }
  }, []);

  recoverRef.current = recoverStream;

  // Watchdog: frames stop arriving without any event on some iOS versions.
  // Coming back from the background is not a stall; give it a full window.
  useEffect(() => {
    let visibleSince = performance.now();
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') visibleSince = performance.now();
    };
    document.addEventListener('visibilitychange', onVisibility);
    const timer = window.setInterval(() => {
      const recorder = recorderRef.current;
      if (!recorder || document.visibilityState !== 'visible') return;
      if (performance.now() - visibleSince < STREAM_STALL_MS) return;
      const silentFor = recorder.msSinceLastCallback();
      if (silentFor > STREAM_STALL_MS) void recoverStream();
      else if (silentFor > 0 && silentFor < 1000) recoveriesRef.current = 0;
    }, 2000);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [recoverStream]);

  useEffect(() => {
    if (options.autoPrepare === false) return;
    void boot(optionsRef.current.facing ?? 'environment');
    return () => {
      void recorderRef.current?.stop();
      streamRef.current?.stop();
      recorderRef.current = null;
      streamRef.current = null;
    };
    // Boot exactly once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flipCamera = useCallback(async () => {
    const next: CameraFacing = facing === 'environment' ? 'user' : 'environment';
    setFacing(next);
    optionsRef.current.onFacingChange?.(next);
    await boot(next);
  }, [boot, facing]);

  const start = useCallback(async () => {
    await recorderRef.current?.start();
  }, []);

  const pause = useCallback((reason: PauseReason = 'user') => recorderRef.current?.pause(reason), []);
  const resume = useCallback(() => recorderRef.current?.resume(), []);

  const stop = useCallback(async () => {
    await recorderRef.current?.stop();
    streamRef.current?.stop();
    streamRef.current = null;
  }, []);

  const calibrate = useCallback(async (): Promise<Calibration | null> => {
    const recorder = recorderRef.current;
    if (!recorder) return null;
    const result = await recorder.calibrate();
    if (result.sampleCount === 0) return null;
    return {
      cvaBaseline: result.cvaBaseline,
      trunkBaseline: result.trunkBaseline,
      protBaseline: result.protBaseline,
      measuredAt: Date.now(),
      sampleCount: result.sampleCount,
    };
  }, []);

  const disagreementCount = useCallback(() => recorderRef.current?.disagreementCount ?? 0, []);

  return {
    videoRef,
    tick,
    phase,
    pauseReason,
    error,
    facing,
    streamReady,
    previewReady,
    modelReady,
    ready: streamReady && modelReady,
    start,
    pause,
    resume,
    stop,
    flipCamera,
    calibrate,
    disagreementCount,
  };
}

/** Resolves after the browser has painted the current frame. */
function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}
